import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaTrash } from 'react-icons/fa';

interface Video {
  id: number;
  title: string;
  url: string;
}

const YT = () => {
  const api = process.env.REACT_APP_API_URL as string;
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${api}/auth/YTVideo`);
      setVideos(res.data);
    } catch (err) {
      console.error('Error fetching videos:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !url) {
      alert('Please fill in all fields.');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${api}/auth/YTVideo`, { title, url });
      setTitle('');
      setUrl('');
      fetchVideos();
    } catch (err) {
      console.error('Error adding video:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axios.delete(`${api}/auth/YTVideo/${id}`);
      fetchVideos();
    } catch (err) {
      console.error('Error deleting video:', err);
    }
  };

  const extractYouTubeID = (url: string): string => {
    const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : '';
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-left text-black mb-12">YouTube Videos</h1>

      {/* Form Section */}
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 rounded-xl shadow-md p-8 mb-14"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">Video Title</label>
            <input
              type="text"
              placeholder="Enter video title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">YouTube URL</label>
            <input
              type="text"
              placeholder="e.g. https://youtu.be/abc123xyz"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full md:w-40 bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 font-semibold transition duration-300"
        >
          {loading ? 'Saving...' : 'Add Video'}
        </button>
      </form>

      {/* Videos Section */}
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Uploaded Videos</h2>
      <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => {
          const videoId = extractYouTubeID(video.url);
          return (
            <div key={video.id} className="bg-white rounded-lg border shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-md font-medium text-gray-800 truncate">{video.title}</h3>
                <button
                  onClick={() => handleDelete(video.id)}
                  className="text-red-600 hover:text-red-800 transition"
                  title="Delete video"
                >
                  <FaTrash />
                </button>
              </div>

              {videoId ? (
                <iframe
                  className="w-full aspect-video"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={video.title}
                  allowFullScreen
                />
              ) : (
                <div className="p-4 text-red-500 text-sm">⚠ Invalid YouTube URL</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default YT;
